import React, { useMemo } from 'react';
import { format, differenceInMinutes } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Box as MuiBox, Tooltip, CircularProgress, useTheme } from '@mui/material';

import {
  ExpandMore as ExpandMoreIcon,
  PermIdentity as PermIdentityIcon,
} from '@mui/icons-material';

import { getDateFormat, cutDownText } from 'lib/utils';
import useDialog from 'lib/hooks/useDialog';
import { meetingTypesData } from 'lib/enums/meetingTypes';

import { Avatar, Chip, Icon, Space, Typography } from 'components/ui';

import { careTeamMemberRouteTemplates } from 'routing/routeTemplates';

import {
  SAccordion,
  SEventPreview,
  SMuiAccordionSummary,
} from 'pages/SchedulePage/styled/eventCard';
import {
  actions as calendarActions,
  getModuleState,
} from '../../services/calendar/index';
import ChooseDateDialog from '../SchedulePage/ChooseDateDialog/index';
import useRescheduleAppointment from './hooks/useRescheduleAppointment';

import { eventUserStatuses } from 'lib/enums/eventUserStatuses';
import { useStatusObject } from './utils/statusObject';
import { getTimeZones } from '@vvo/tzdb';
import { dateFnsLocales } from 'lib/utils/getDateFnsLocales';
import { formatInTimeZone } from 'date-fns-tz';

const getCardDate = (date, t) => {
  const dateFormat = getDateFormat(date);
  const weekday = t(format(dateFormat, 'EEE'));
  const day = format(dateFormat, 'dd');
  const month = t(format(dateFormat, 'MMMM')).slice(0, 3);
  const year = format(dateFormat, 'yyy');

  return `${weekday} ${day} ${month} ${year} `;
};

function Card(props) {
  const { data, isCancelled, openDetailsDialog } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const calendarState = useSelector(getModuleState);
  const { t, i18n } = useTranslation();
  const theme = useTheme()
  const { selectedEvent } = calendarState;

  const statusObject = useStatusObject()
  const userTimezoneCode = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const usersTimezone = useMemo(
    () => getTimeZones().find((tz) => tz.group.includes(userTimezoneCode)),
    []
  );

  const chooseDateDialog = useDialog();

  const {
    onRescheduleAppointment,
    loadingRescheduleAppointment,
  } = useRescheduleAppointment();

  const openChooseDateDialog = async (event) => {
    const practitionerId =
      event?.practitionerParticipants &&
      event?.practitionerParticipants[0]?.practitioner?.id;
    
    // Calculate actual appointment duration from event data
    const actualDuration = event?.planifiedDuration || 
      (event?.startDate && event?.endDate ? 
        differenceInMinutes(new Date(event.endDate), new Date(event.startDate)) : 
        30);
    
    const { selectedTime, planifiedDuration } = await chooseDateDialog.open({
      selectedDate: {
        startDate: event?.startDate,
        endDate: event?.endDate,
      },
      selectedDuration: actualDuration,
      isRescheduleMode: true,
      userId: practitionerId,
    });

    if (!selectedTime) return;

    const usersId =
      event?.practitionerParticipants &&
      event?.practitionerParticipants.map(
        ({ practitioner }) => practitioner?.id
      );
    onRescheduleAppointment(event, selectedTime, usersId, planifiedDuration);
  };

  const highlightEvent = (eventId) => {
    dispatch(calendarActions.highlightEvent({ eventId }));
  };

  const openEvent = (event) => {
    dispatch(calendarActions.highlightEvent({ eventId: event.id }));
    openDetailsDialog(event);
  };

  return (
    <SAccordion defaultExpanded={!data.isClosed}>
      <SMuiAccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={data.key}
        id={data.key}
        data-id={data.key}
      >
        <div>
          <Typography variant="h4" gutterBottom>
            {data.title}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {data.description}
          </Typography>
        </div>
      </SMuiAccordionSummary>
      {data?.events?.length === 0 && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Icon
            type="fad"
            icon="clipboard-list"
            size="3x"
            style={{ color: theme.palette.colors.gray300 }}
          />
        </div>
      )}
      <MuiBox style={{ maxHeight: 400, overflow: 'auto' }}>
        {data?.events?.map((event, idx) => {
          const loadings =
            loadingRescheduleAppointment.loadingReschedule &&
            loadingRescheduleAppointment.id === event?.id;

          return (
            <SEventPreview
              data-id={`schedule-card-${idx}`}
              isSelected={selectedEvent.eventId === event.id}
              status={event.status}
              key={event.id}
              onMouseEnter={() => highlightEvent(event.id)}
              onMouseLeave={() => highlightEvent(null)}
              onClick={() => openEvent(event)}
            >
              <div>
                {loadings ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '80px',
                    }}
                  >
                    <CircularProgress size={25} />
                  </div>
                ) : (
                  <div>
                    <MuiBox display="flex" justifyContent="space-between">
                      <Tooltip title={event?.note || ''}>
                        <div>
                          <Typography
                            variant="h5"
                            gutterBottom={
                              !(
                                event?.location?.preferredTimeZone &&
                                event?.location?.preferredTimeZone !==
                                  userTimezoneCode
                              )
                            }
                            style={{
                              textDecoration: isCancelled ? 'line-through' : '',
                            }}
                          >
                            {event?.startDate ? (
                              <span>
                                {getCardDate(event.startDate, t)}
                                &sdot;
                                {format(getDateFormat(event.startDate), ' p', {
                                  locale: dateFnsLocales[i18n.language],
                                }).toLowerCase()}
                              </span>
                            ) : (
                              <span>
                                {getCardDate(event?.possibleDateRange[0], t)}
                                {event?.possibleDateRange?.length > 1 &&
                                  ` - ${getCardDate(
                                    event?.possibleDateRange[
                                      event?.possibleDateRange?.length - 1
                                    ],
                                    t
                                  )}`}
                              </span>
                            )}
                            {/* {`, ${usersTimezone.abbreviation}`} */}
                          </Typography>
                          {event.startDate &&
                          event?.location?.preferredTimeZone &&
                          event?.location?.preferredTimeZone !==
                            userTimezoneCode ? (
                            <Typography fontSize={14} gutterBottom>
                              {`${t('On appointment site')}: ${formatInTimeZone(
                                event.startDate,
                                event?.location?.preferredTimeZone,
                                'P p',
                                {
                                  locale: dateFnsLocales[i18n.language],
                                }
                              )}`}
                            </Typography>
                          ) : null}
                          <Typography variant="body1">
                            {`${event?.patientParticipant?.patient?.name?.firstName?.[0]} ${event?.patientParticipant?.patient?.name?.lastName}`}
                          </Typography>
                          <MuiBox display="flex">
                            <Typography
                              sx={{ textTransform: 'capitalize' }}
                              variant="body1"
                              color="textSecondary"
                            >
                              {t(meetingTypesData[event.eventType]?.label)}
                            </Typography>
                          </MuiBox>
                          <Typography
                            variant="subtitle1"
                            color="textPrimary"
                            gutterBottom
                            sx={{ marginTop: '10px' }}
                          >
                            {event.note &&
                              cutDownText({
                                text: event.note,
                                symbolsQtty: 80,
                              })}
                          </Typography>
                        </div>
                      </Tooltip>
                      <Avatar
                        src={event?.patientParticipant?.patient?.photoUrl}
                      />
                    </MuiBox>

                    <MuiBox
                      display="flex"
                      justifyContent="space-between"
                      mt={1}
                    >
                      <Space display="flex">
                        <Chip
                          onClick={() => {}}
                          sx={{
                            borderRadius: '10px',
                            textTransform: 'capitalize',
                          }}
                          $color={
                            event.status === eventUserStatuses.booked
                              ? statusObject.APPROVED.hexColor
                              : statusObject.AWAITING.hexColor
                          }
                          $backgroundColor={
                            event.status === eventUserStatuses.booked
                              ? statusObject.APPROVED.hexBgColor
                              : statusObject.AWAITING.hexBgColor
                          }
                          label={t(event?.status)}
                        />
                      </Space>
                      <Space sx={{ display: 'flex' }}>
                        <Tooltip
                          title={t('Reschedule date')}
                          aria-label="reschedule"
                          placement="top"
                        >
                          <MuiBox marginRight={1}>
                            <Chip
                              onClick={(e) => {
                                e.stopPropagation();
                                openChooseDateDialog(event);
                              }}
                              icon={<Icon icon="calendar-day" />}
                              $color={theme.palette.colors.blue100}
                              $backgroundColor={theme.palette.colors.blue200}
                            />
                          </MuiBox>
                        </Tooltip>
                        <Tooltip
                          title={t('Patient record')}
                          aria-label="patient-record"
                          placement="top"
                        >
                          <MuiBox display="flex">
                            <Chip
                              fontSize="20px"
                              data-id="card-router-to-patient-button"
                              onClick={() =>
                                history.push(
                                  `/patientRecord/${event?.patientParticipant?.patient?.id}`,
                                  {
                                    from: {
                                      pathname:
                                        careTeamMemberRouteTemplates.schedulePage,
                                      title: 'Schedule',
                                    },
                                  }
                                )
                              }
                              $color={theme.palette.colors.blue100}
                              $backgroundColor={theme.palette.colors.blue200}
                              icon={<PermIdentityIcon />}
                            />
                          </MuiBox>
                        </Tooltip>
                      </Space>
                    </MuiBox>
                  </div>
                )}
              </div>
            </SEventPreview>
          );
        })}
      </MuiBox>

      {chooseDateDialog.isOpen && (
        <Form onSubmit={console.log}>
          {({ values }) => (
            <ChooseDateDialog
              isOpen={chooseDateDialog.isOpen}
              close={chooseDateDialog.close}
              initialData={chooseDateDialog.initialData}
            />
          )}
        </Form>
      )}
    </SAccordion>
  );
}

export default Card;
